import React, {useState} from 'react';
import logo from "../../assets/logo.png";
import './Login.css'; 
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useLanguage, getLoginTranslation, getMainTranslation } from '../../hooks/useLanguage';
import { Mail, Lock, ChevronDown } from 'lucide-react';
import english from '../../assets/united-states.png';
import spanish from '../../assets/spain.png';
import handWave from '../../assets/hand-wave.gif';
import { HowItWorksContent } from '../Dashboard/HowItWorksContent';

const API_BASE_URL = `${process.env.REACT_APP_DOCS_API_KEY}`;

const Login = ({isDarkMode, email, password, setEmail, setPassword}) => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSpanish, setIsSpanish] = useLanguage();

  const validateForm = () => {
    if (!email || !password) {
      setError(getLoginTranslation('emailAndPasswordRequired', isSpanish));
      return false;
    }
    setError('');
    return true;
  };

  const fetchCurrentUser = async (token) => {
    try {
      const response = await fetch(`https://${API_BASE_URL}/api/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user details');
      }

      const data = await response.json();
      
      // Set user_id in sessionStorage
      sessionStorage.setItem('user_id', data.id);
      return data;
    } catch (err) {
      console.error("Error fetching user:", err.message);
      return null;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');

    try {
      const url = `https://${process.env.REACT_APP_DOCS_API_KEY}/api/auth/login`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          password: password
        }),
      });

      const responseData = await response.json();

      if (response.ok && responseData) {
        
        const token = responseData.access_token;
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('email', email);
        
        // Store refresh token
        if (responseData.refresh_token) {
          sessionStorage.setItem('refresh_token', responseData.refresh_token);
        }
        
        if (responseData.expires) {
          const currentTime = new Date().getTime();
          const expirationTime = currentTime + parseInt(responseData.expires, 10);
          sessionStorage.setItem('expires', expirationTime.toString());
        } else {
          // Default to 1 hour if expires is not provided
          const defaultExpiry = new Date().getTime() + (60 * 60 * 1000);
          sessionStorage.setItem('expires', defaultExpiry.toString());
        }
        
        const userData = await fetchCurrentUser(token);
        
        if (userData) {
          // Redirect to external URL with email and password as parameters
          const redirectUrl = new URL('https://app.cargoflow.nl/login');
          redirectUrl.searchParams.set('email', email);
          redirectUrl.searchParams.set('password', password);
          window.location.href = redirectUrl.toString();
          toast.success(getLoginTranslation('successfullyLoggedIn', isSpanish));
        } else {
          setError(getLoginTranslation('couldNotRetrieveUserInformation', isSpanish));
        }
      } else {
        const errorMessage = responseData.error?.message || 
                            responseData.detail?.[0]?.msg ||
                            getLoginTranslation('authenticationFailed', isSpanish);
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Error during login:', error);
      setError(getLoginTranslation('errorOccurred', isSpanish));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`login-container ${isDarkMode ? 'dark-mode' : ''}`}>
      <button
              onClick={() => { setIsSpanish(!isSpanish) }}
              className={`absolute top-4 right-4 cursor-pointer h-10 flex items-center gap-2 py-2 px-4 rounded-full transition-colors ${isSpanish ? "bg-yellow-100" : "bg-blue-100"}`}
            >
              <img src={isSpanish ? spanish : english} alt="Language" className="h-5" /> 
              <p className="text-sm">{isSpanish ? getMainTranslation('esp', isSpanish) : getMainTranslation('eng', isSpanish)}</p>
            </button>

            <div className="flex flex-col items-center justify-center h-screen">
      <form className="login-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <div className='flex w-full justify-center'>
            <img src={logo} alt="Logo" className="logo-image !w-auto !h-9" /> 
          </div>
          <h1 className='text-4xl font-bold mb-8 mt-4 text-center font-gdSage'>{getLoginTranslation('loginToCustomFlows', isSpanish)}</h1>
          <div className='flex items-center gap-2 border-container-force rounded-full px-4 py-3'>
            <Mail size={20} className={`${email ? 'stroke-[#121212]' : 'stroke-[#9DA3AF]'}`} />
          <input 
            type="email" 
            id="email" 
            className="form-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required 
            placeholder={getLoginTranslation('email', isSpanish)}
          />
          </div>
        </div>
        <div className="form-group">
          <div className='flex items-center gap-2 border-container-force rounded-full px-4 py-3'>
            <Lock size={20} className={`${password ? 'stroke-[#121212]' : 'stroke-[#9DA3AF]'}`}/>
          <input 
            type="password" 
            id="password" 
            className="form-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required 
            placeholder={getLoginTranslation('password', isSpanish)}
          />
          </div>
        </div>
        {error && <p className="mb-4 text-red-500">{error}</p>}
        
        <div className='flex justify-end'>
          <button type="submit" disabled={loading} className='rounded-full mt-2 login-button py-4 px-4 mr-0 w-full'>
            {loading ? getLoginTranslation('loggingIn', isSpanish) : getLoginTranslation('logIn', isSpanish)}
          </button>
        </div>
        
        {/* <div className="text-center mt-4">
          <p>No account? <Link to="/signup" className="text-primary">Register here</Link></p>
        </div> */}
        
        <div className="text-center mt-6">
          <span className="text-[#121212] flex items-center gap-1 justify-center">
            {isSpanish ? '¿Quieres probarlo primero?' : 'Want to try first?'} <Link to="/demo" className="demo-text green-gradient-2 text-green-600 hover:text-green-700 font-medium"> 
            <span className="flex items-center gap-1 try-demo-text h-9 px-1 transition-all duration-200 overflow-hidden">
            <span className="shine">
            {isSpanish ? 'Probar Demo' : 'Try Demo'}
            </span>
            <span className='relative size-7 flex wave-container transition-all duration-200'>
            <span className='size-7 relative'>
              <img src={handWave} alt="Hand Wave" className="size-7 animate-bounce-wave" />
            </span>
            </span>
            </span>
            
            </Link>
          </span>
        </div>
      </form>
      </div>

<div className="flex flex-col items-center mt-[-8rem] gap-4 mb-40">
      <div className=" animate-bounce-arrow">
        <ChevronDown 
          size={32} 
          className="text-gray-600 cursor-pointer hover:text-gray-800 transition-colors"
          onClick={() => {
            const howItWorksSection = document.querySelector('.bg-secondary.rounded-3xl');
            if (howItWorksSection) {
              howItWorksSection.scrollIntoView({ behavior: 'smooth' });
            }
          }}
        />
      </div>

      <div className="bg-secondary rounded-3xl p-10 w-full">
      <h2 className="text-2xl font-bold mb-8 text-center font-gdSage">
        {isSpanish ? 'Cómo Funciona' : 'How It Works'}
      </h2>
        <HowItWorksContent />
      </div>
      </div>
      
    </div>
  );
};

export default Login;